import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Wallet, Upload, Loader2, Search, Plus, ChevronRight, Camera,
  CheckCircle2, Clock, XCircle, RefreshCcw, Banknote, BarChart3, ArrowLeftRight,
} from "lucide-react";
import api from "@/lib/api";

const STATUT_LABELS = {
  brouillon: "Brouillon",
  a_valider: "À valider",
  comptabilisee: "Comptabilisée",
  classee: "Classée",
  rejetee: "Rejetée",
};
const STATUT_COLORS = {
  brouillon: "bg-gray-100 text-gray-600 border-gray-200",
  a_valider: "bg-amber-50 text-amber-700 border-amber-200",
  comptabilisee: "bg-emerald-50 text-emerald-700 border-emerald-200",
  classee: "bg-blue-50 text-blue-700 border-blue-200",
  rejetee: "bg-red-50 text-red-700 border-red-200",
};

const MODE_LABELS = {
  cash: "Caisse",
  carte_raiffeisen: "Carte Raiffeisen",
};

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

export default function DepensesRapides() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [byStatut, setByStatut] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("a_valider");
  const [q, setQ] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "all") params.set("statut", tab);
      if (q) params.set("q", q);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));
      const { data } = await api.get(`/depenses-rapides?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setByStatut(data.by_statut || {});
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [tab, q, page]);

  useEffect(() => { setPage(1); }, [tab, q]);
  useEffect(() => { reload(); }, [reload]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 5) { toast.error("Maximum 5 fichiers par dépense"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/depenses-rapides/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Dépense importée — ${data.reference_interne}. Analyse OCR…`);
      try {
        await api.post(`/depenses-rapides/${data.id}/ocr`);
        toast.success("Analyse OCR terminée — vérifiez et validez");
      } catch {
        toast.error("OCR échoué — vous pouvez réessayer depuis la fiche");
      }
      navigate(`/depenses-rapides/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur import");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const Counter = ({ statut }) => {
    const n = byStatut[statut] || 0;
    if (!n) return null;
    return <Badge className="ml-2 bg-gray-200 text-gray-700 border-0 h-5 px-1.5 text-[10px]">{n}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="page-depenses-rapides">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-orange-500" />
            Dépenses rapides
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tickets · Quittances · Reçus carte · Achats déjà payés à comptabiliser
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/achats/statistiques")}
            data-testid="dep-stats-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" /> Statistiques
          </Button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleUpload}
            data-testid="dep-camera-input"
          />
          <Button
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="sm:hidden border-orange-300 text-orange-700"
            data-testid="dep-camera-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Photo
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,image/jpeg,image/jpg,image/png,image/heic,image/heif,application/pdf"
            className="hidden"
            onChange={handleUpload}
            data-testid="dep-upload-input"
          />
          {/* iter250 — mobile : l'import passe sous le titre, centré et pleine largeur.
              iter254 — sur desktop il faisait doublon avec la zone de dépôt juste
              en dessous (même action) : on ne le garde donc que sur mobile. */}
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-orange-500 hover:bg-orange-600 text-white w-full justify-center order-last mt-1 sm:hidden"
            data-testid="dep-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Analyse…" : "Importer un ticket / quittance"}
          </Button>
        </div>
      </div>

      {/* Drag & drop avec capture mobile */}
      <Card
        className="border-2 border-dashed border-orange-200 hover:border-orange-400 transition-colors p-6 mb-5 text-center cursor-pointer bg-orange-50/30"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const dt = new DataTransfer();
          Array.from(e.dataTransfer.files || []).forEach((f) => dt.items.add(f));
          if (fileInputRef.current) {
            fileInputRef.current.files = dt.files;
            handleUpload({ target: { files: dt.files } });
          }
        }}
        data-testid="dep-drop-zone"
      >
        <div className="flex items-center justify-center gap-3 text-orange-700">
          <Camera className="w-6 h-6" />
          <span className="text-sm">
            <span className="hidden sm:inline font-semibold underline">Cliquez pour importer</span>
            <span className="hidden sm:inline"> ou glissez vos fichiers — </span>
            Photo · PDF · Image — l&apos;IA propose le compte et la TVA, vous validez en 1 clic
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Idéal pour : carburant, repas, fournitures, petits achats comptant
        </p>
      </Card>

      {/* Recherche */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (réf, fournisseur, description…)"
            className="pl-10"
            data-testid="dep-search"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        {/* iter254 — mobile : menu déroulant au lieu de la bande d'onglets */}
        <div className="sm:hidden mb-3">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-full" data-testid="dep-tab-menu">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[
                { value: "all", label: "Toutes", statut: null },
                { value: "a_valider", label: "À valider", statut: "a_valider" },
                { value: "comptabilisee", label: "Comptabilisées", statut: "comptabilisee" },
                { value: "classee", label: "Classées (carte)", statut: "classee" },
                { value: "rejetee", label: "Rejetées", statut: "rejetee" },
              ].map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`dep-tab-menu-${t.value}`}>
                  {t.label}{(byStatut[t.statut] || 0) > 0 ? ` (${byStatut[t.statut]})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TabsList className="hidden sm:flex bg-white border border-gray-200 p-1 h-auto flex-wrap" data-testid="dep-tabs">
          <TabsTrigger value="all" data-testid="dep-tab-all">Toutes <Counter statut={null} /></TabsTrigger>
          <TabsTrigger value="a_valider" data-testid="dep-tab-a-valider">À valider <Counter statut="a_valider" /></TabsTrigger>
          <TabsTrigger value="comptabilisee" data-testid="dep-tab-comptabilisee">
            Comptabilisées <Counter statut="comptabilisee" />
          </TabsTrigger>
          <TabsTrigger value="classee" data-testid="dep-tab-classee">
            Classées (carte) <Counter statut="classee" />
          </TabsTrigger>
          <TabsTrigger value="rejetee" data-testid="dep-tab-rejetee">Rejetées <Counter statut="rejetee" /></TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : items.length === 0 ? (
            <Card className="p-12 text-center text-gray-400">
              <Wallet className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Aucune dépense rapide</p>
              <p className="text-xs text-gray-400 mt-1">
                Prenez en photo un ticket de caisse ou une quittance pour commencer.
              </p>
              <Button
                variant="outline" size="sm" className="mt-4"
                onClick={() => fileInputRef.current?.click()}
                data-testid="dep-empty-upload-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Importer un ticket
              </Button>
            </Card>
          ) : (
            <Card className="p-0 overflow-hidden">
              {/* iter256 — la table (avec en-tête) est réservée à l'ordinateur */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Référence</th>
                    <th className="px-3 py-2 text-left">Fournisseur / Commerce</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Montant TTC</th>
                    <th className="px-3 py-2 text-left">Compte</th>
                    <th className="px-3 py-2 text-left">Paiement</th>
                    <th className="px-3 py-2 text-center">Statut</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <tr
                      key={d.id}
                      className="border-t border-gray-100 hover:bg-orange-50/30 cursor-pointer"
                      onClick={() => navigate(`/depenses-rapides/${d.id}`)}
                      data-testid={`dep-row-${d.id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-gray-700">{d.reference_interne}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {d.fournisseur_nom || <span className="text-gray-300 italic">non renseigné</span>}
                        {d.description && (
                          <div className="text-[11px] text-gray-500 mt-0.5 truncate max-w-xs">{d.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{fmtDate(d.date_depense)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">
                        {d.montant_ttc != null ? `${fmtChf(d.montant_ttc)} ${d.devise || "CHF"}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs font-mono">{d.compte_charge || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {d.mode_paiement ? (
                          <span className="inline-flex items-center gap-1">
                            {d.mode_paiement === "cash" ? <Banknote className="w-3 h-3" /> : <RefreshCcw className="w-3 h-3" />}
                            {MODE_LABELS[d.mode_paiement] || d.mode_paiement}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={`text-[10px] ${STATUT_COLORS[d.statut] || STATUT_COLORS.brouillon}`}>
                          {STATUT_LABELS[d.statut] || d.statut}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-400">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {tab === "a_valider" && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Transférer ${d.reference_interne} vers les Factures Fournisseurs ?\nLa DR sera supprimée et une FF créée dans 'À valider'.`)) return;
                                try {
                                  const { data } = await api.post(`/depenses-rapides/${d.id}/transfer-to-factures-fournisseurs`);
                                  toast.success(`Transféré vers FF ${data.to_ff_reference}`);
                                  reload();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Erreur transfert");
                                }
                              }}
                              className="p-1 rounded hover:bg-blue-100 text-blue-600"
                              title="Transférer vers Factures Fournisseurs"
                              data-testid={`dep-transfer-ff-${d.id}`}
                            >
                              <ArrowLeftRight className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* iter256 — mobile : pas d'en-tête de table, 2 lignes par dépense
                  (référence, date, statut, fournisseur, compte/mode, montant TTC) */}
              <div className="md:hidden divide-y divide-gray-100" data-testid="dep-list-mobile">
                {items.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => navigate(`/depenses-rapides/${d.id}`)}
                    className="w-full text-left px-3 py-2.5 hover:bg-orange-50/40 active:bg-orange-50"
                    data-testid={`dep-card-${d.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <code className="text-xs font-mono font-semibold text-gray-800">{d.reference_interne}</code>
                        <span className="text-[11px] text-gray-500 whitespace-nowrap">{fmtDate(d.date_depense)}</span>
                      </div>
                      <Badge className={`text-[10px] flex-shrink-0 ${STATUT_COLORS[d.statut] || STATUT_COLORS.brouillon}`}>
                        {STATUT_LABELS[d.statut] || d.statut}
                      </Badge>
                    </div>
                    <div className="flex items-end justify-between gap-2 min-w-0 mt-0.5">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-900 font-medium truncate">
                          {d.fournisseur_nom || <span className="text-gray-300 italic">non renseigné</span>}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {d.description || (d.compte_charge ? `Compte ${d.compte_charge}` : "—")}
                        </div>
                      </div>
                      <div className="font-mono text-sm font-semibold text-gray-900 flex-shrink-0 whitespace-nowrap">
                        {d.montant_ttc != null ? `${fmtChf(d.montant_ttc)} ${d.devise || "CHF"}` : "—"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-600">
                  <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} sur {total}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                      data-testid="dep-prev-page">
                      Précédent
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setPage((p) => p + 1)} disabled={page * PAGE_SIZE >= total}
                      data-testid="dep-next-page">
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
